import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/Field";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

const DayRow = ({ idx, intervals, onChange }) => {
  const upd = (i, k, v) => onChange(intervals.map((iv, j) => (j === i ? { ...iv, [k]: v } : iv)));
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[#e2e8f0] py-3 last:border-0" data-testid={`availability-day-${idx}`}>
      <div className="w-24 text-sm font-semibold">{DAYS[idx]}</div>
      <div className="flex flex-1 flex-wrap gap-2">
        {intervals.length === 0 && <span className="py-2 text-xs text-slate-400">Non disponibile</span>}
        {intervals.map((iv, i) => (
          <div key={i} className="flex items-center gap-1 rounded-full bg-[#edf2ef] px-2 py-1">
            <input type="time" className="bg-transparent text-xs outline-none" value={iv.start} onChange={(e) => upd(i, "start", e.target.value)} data-testid={`availability-${idx}-${i}-start`} />
            <span className="text-xs">–</span>
            <input type="time" className="bg-transparent text-xs outline-none" value={iv.end} onChange={(e) => upd(i, "end", e.target.value)} data-testid={`availability-${idx}-${i}-end`} />
            <button type="button" onClick={() => onChange(intervals.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`availability-${idx}-${i}-remove`}><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <button type="button" className="btn-ghost text-[#3d655b]" onClick={() => onChange([...intervals, { start: "09:00", end: "13:00" }])} data-testid={`availability-${idx}-add`}><Plus className="h-4 w-4" /> Fascia</button>
    </div>
  );
};

export function AvailabilityDialog({ open, onOpenChange }) {
  const [av, setAv] = useState(null);
  useEffect(() => { if (open) api.get("/settings/availability").then((r) => setAv(r.data)); }, [open]);

  const save = async () => {
    try {
      await api.put("/settings/availability", { ...av, slot_minutes: Number(av.slot_minutes), days_ahead: Number(av.days_ahead) });
      toast.success("Orari di disponibilità salvati");
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="availability-dialog">
        <DialogHeader><DialogTitle>Orari disponibili per le prenotazioni</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Quando un paziente disdice dal promemoria, può scegliere un nuovo orario libero tra queste fasce. Gli orari già occupati da appuntamenti o impegni dell'agenda personale vengono esclusi automaticamente (un impegno senza orario blocca tutta la giornata).</p>
        {av && (
          <div className="space-y-4">
            <div>{DAYS.map((_, i) => <DayRow key={i} idx={i} intervals={av.week[String(i)] || []} onChange={(v) => setAv({ ...av, week: { ...av.week, [String(i)]: v } })} />)}</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Intervallo tra gli orari (min)"><input type="number" min="10" max="240" className="field" value={av.slot_minutes} onChange={(e) => setAv({ ...av, slot_minutes: e.target.value })} data-testid="availability-slot-minutes" /></Field>
              <Field label="Prenotabile fino a (giorni)"><input type="number" min="1" max="90" className="field" value={av.days_ahead} onChange={(e) => setAv({ ...av, days_ahead: e.target.value })} data-testid="availability-days-ahead" /></Field>
            </div>
            <div className="flex justify-end"><button className="btn-primary" onClick={save} data-testid="availability-save-button">Salva orari</button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
